import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import type {
  Audience,
  LeaderboardBoardsResponse,
  LeaderboardPreview,
  PreviewColumn,
} from "../../../../types/leaderboard";
import { windowBadge } from "../leaderboard-rules";
import { findUnusual } from "../unusual-values";
import { MEASUREMENT_LABELS } from "./ChallengeTable";

// Table header labels for member-board column keys.
const LABEL_KEYS: Record<string, [string, string?]> = {
  workouts: ["challengeReport.table.workouts"],
  completionRate: ["challengeReport.table.completionRate", "Completion %"],
  avgDailySteps: ["challengeReport.table.avgSteps"],
  habitsAdherence: ["challengeReport.table.habitsPercent"],
  totalVolume: ["challengeReport.table.totalVolume", "Total volume"],
  totalReps: ["challengeReport.table.totalReps", "Total reps"],
  totalSets: ["challengeReport.table.totalSets", "Total sets"],
  avgSessionRpe: ["challengeReport.table.avgSessionRpe", "Avg session RPE"],
  avgTrainingReadiness: [
    "challengeReport.table.avgTrainingReadiness",
    "Avg training readiness",
  ],
  avgWaterIntake: ["challengeReport.table.avgWaterIntake", "Avg water intake"],
  bodyweight: ["challengeReport.table.bwChange"],
};

const PERCENT_KEYS = new Set(["completionRate", "habitsAdherence"]);
const LOAD_KEYS = new Set(["bodyweight", "totalVolume"]);

/**
 * "Preview in app": the board as members rank it — abbreviated names,
 * initials only, no coach-only columns, every column on its own window.
 */
interface MemberPreviewTableProps {
  preview: LeaderboardPreview | undefined;
  units: "metric" | "imperial";
  audience: Audience;
  // Publishers can put members on hold: off every board (e.g. suspicious
  // numbers) until returned.
  canHold: boolean;
  boardsData: LeaderboardBoardsResponse | undefined;
}

const MemberPreviewTable = ({
  preview,
  units,
  audience,
  canHold,
  boardsData,
}: MemberPreviewTableProps) => {
  const { t } = useTranslation("coaching-portal");
  const {
    columns = [],
    rows = [],
    ranking,
    memberCount,
    minMembers,
  } = preview || {};
  const [sortKey, setSortKey] = useState(columns[0]?.key);

  const columnLabel = (column: PreviewColumn): string => {
    if (column.label) return column.label;
    if (column.key.startsWith("bm:")) {
      return MEASUREMENT_LABELS[column.key.slice(3)] || column.key;
    }
    const [key, fallback] = LABEL_KEYS[column.key] || [column.key];
    return t(key, fallback);
  };

  const isLoad = (key: string) => LOAD_KEYS.has(key) || key.startsWith("rm:");

  const formatValue = (key: string, value: number | null | undefined) => {
    if (value == null) return "—";
    if (PERCENT_KEYS.has(key)) return `${value}%`;
    if (isLoad(key) && units === "imperial") {
      return Math.round(value * 2.20462 * 10) / 10;
    }
    return typeof value === "number" ? value.toLocaleString() : value;
  };

  // "242,559.6 kg" — for reasons, where the column header isn't in sight.
  const formatWithUnit = (key: string, value: number) =>
    isLoad(key)
      ? `${formatValue(key, value).toLocaleString()} ${units === "imperial" ? "lb" : "kg"}`
      : String(formatValue(key, value));

  const unusual = useMemo(
    () =>
      canHold
        ? findUnusual(
            columns.map((c) => c.key),
            rows
          )
        : new Map(),
    [canHold, columns, rows]
  );

  const unusualReason = (userId: string) => {
    const flag = unusual.get(userId);
    if (!flag) return null;
    const column = columns.find((c) => c.key === flag.key) as PreviewColumn;
    return t(
      "challengeReport.hold.unusualReason",
      "{{ratio}}× the next-highest {{column}} on this board ({{next}}). Often a typo or a wrong unit.",
      {
        ratio: Math.round(flag.ratio),
        column: columnLabel(column).toLowerCase(),
        next: formatWithUnit(flag.key, flag.next),
      }
    );
  };

  const putOnHold = (row: { userId: string; name: string }) => {
    const flag = unusual.get(row.userId);
    const column =
      flag && (columns.find((c) => c.key === flag.key) as PreviewColumn);
    submit("/actions/leaderboard-holds", {
      method: "POST",
      body: {
        userId: row.userId,
        audience,
        name: row.name,
        ...(flag && {
          why: `${columnLabel(column)} ${formatWithUnit(flag.key, flag.value)}`,
        }),
      },
    });
  };

  const sortedRows = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey);
    if (!column) return rows;
    const sign = column.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a.values[sortKey];
      const bv = b.values[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      return sign * (av - bv);
    });
  }, [rows, columns, sortKey]);

  return (
    <>
      <p className="challenge-report__member-view-note">
        <b>{t("challengeReport.preview.title", "App view.")}</b>{" "}
        {t(
          "challengeReport.preview.note",
          "Every column on its own rule, coach-only columns dropped, names abbreviated. This is how the board ranks people in the app — your date range doesn't apply here."
        )}
        {!ranking &&
          ` ${t(
            "challengeReport.preview.notRanking",
            "Not ranking yet: {{count}} of {{min}} people on the board.",
            { count: memberCount, min: minMembers }
          )}`}
      </p>
      {/* biome-ignore lint/a11y/useValidAriaRole: semantic.css styles report tables by role, same as ChallengeTable */}
      <section role="table-wrapper" data-borders-inside>
        <div style={{ overflowX: "auto", borderRadius: "1rem" }}>
          {/* biome-ignore lint/a11y/useValidAriaRole: semantic.css styles report tables by role, same as ChallengeTable */}
          <table role="report-table" className="challenge-report__table">
            {/* biome-ignore lint/a11y/useValidAriaRole: semantic.css styles report tables by role, same as ChallengeTable */}
            <thead role="table-header">
              <tr>
                <th>{t("challengeReport.table.participant")}</th>
                {columns.map((column) => (
                  <th key={column.key}>
                    <button
                      type="button"
                      data-button-type="text"
                      className="p-0! font-[inherit] text-[inherit]"
                      onClick={() => setSortKey(column.key)}
                    >
                      {columnLabel(column)}
                    </button>
                    <span className="challenge-report__col-window">
                      {windowBadge(column.window) || "12M"}
                    </span>
                  </th>
                ))}
                {canHold && (
                  <th>
                    <span className="flex items-center gap-1">
                      {t("challengeReport.hold.rankingHeader", "Ranking")}
                      <ToolTip
                        id="leaderboard-hold-help"
                        place="left"
                        html={`<div class="challenge-report__tip"><b>${t("challengeReport.hold.helpTitle", "Put on hold")}</b>${t(
                          "challengeReport.hold.helpBody",
                          "Takes someone off every public leaderboard while you check their numbers. Use it for a mis-logged set, a test account or a result you want to verify."
                        )}<ul class="challenge-report__hold-help">${[
                          t(
                            "challengeReport.hold.helpNothingDeleted",
                            "Nothing is deleted or edited"
                          ),
                          t(
                            "challengeReport.hold.helpNotNotified",
                            "They aren't notified and still see their own stats"
                          ),
                          t(
                            "challengeReport.hold.helpReturn",
                            "Return them any time from On hold below"
                          ),
                        ]
                          .map((line) => `<li>${line}</li>`)
                          .join("")}</ul></div>`}
                      >
                        <SvgIcon
                          src="/img/new-icons/question-circle.svg"
                          alt={t(
                            "challengeReport.hold.helpTitle",
                            "Put on hold"
                          )}
                          style={{}}
                        />
                      </ToolTip>
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr
                  key={row.userId}
                  className={
                    unusual.has(row.userId)
                      ? "challenge-report__row--unusual"
                      : undefined
                  }
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="challenge-report__initials">
                        {row.initials}
                      </span>
                      {row.name}
                    </div>
                  </td>
                  {columns.map((column) => (
                    <td key={column.key}>
                      {formatValue(column.key, row.values[column.key])}
                      {unusual.get(row.userId)?.key === column.key && (
                        <ToolTip
                          id={`unusual-${row.userId}`}
                          className="inline-block ml-2"
                          html={`<div class="challenge-report__tip"><b>${t("challengeReport.hold.worthACheck", "Worth a check")}</b>${unusualReason(row.userId)}</div>`}
                        >
                          <span className="challenge-report__unusual">
                            {t("challengeReport.hold.unusual", "Unusual")}
                          </span>
                        </ToolTip>
                      )}
                    </td>
                  ))}
                  {canHold && (
                    <td className="text-right">
                      <button
                        type="button"
                        data-button-type="secondary"
                        className="challenge-report__hold-btn"
                        onClick={() => putOnHold(row)}
                      >
                        {t("challengeReport.hold.putOnHold", "Put on hold")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {canHold && <HeldPanel audience={audience} boardsData={boardsData} />}
    </>
  );
};

interface HeldPanelProps {
  audience: Audience;
  boardsData: LeaderboardBoardsResponse | undefined;
}

/** Members on hold (coach's call) and opted out (member's own call). */
const HeldPanel = ({ audience, boardsData }: HeldPanelProps) => {
  const { t } = useTranslation("coaching-portal");
  const { held = [], optedOut = [] } = boardsData || {};
  const [tab, setTab] = useState<"held" | "optedOut">("held");

  const hold = (body: Record<string, string>) =>
    submit("/actions/leaderboard-holds", {
      method: "POST",
      body: { audience, ...body },
    });

  return (
    <section className="challenge-report__held">
      <div className="challenge-report__held-head">
        <div className="flex-1 min-w-64">
          <h3>
            {t("challengeReport.hold.title", "On hold")}{" "}
            <span className="challenge-report__held-count">{held.length}</span>
          </h3>
          <p>
            {t(
              "challengeReport.hold.description",
              "Not ranked on any public leaderboard until you return them. Their workouts, PRs and stats are untouched, and they still see their own numbers in the app."
            )}
          </p>
        </div>
        <div className="challenge-report__held-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "held"}
            onClick={() => setTab("held")}
          >
            {t("challengeReport.hold.tabHeld", "On hold by you")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "optedOut"}
            onClick={() => setTab("optedOut")}
          >
            {t("challengeReport.hold.tabOptedOut", "Opted out by athlete")}
          </button>
        </div>
      </div>

      {tab === "held" &&
        (held.length === 0 ? (
          <p className="challenge-report__held-empty">
            <b>{t("challengeReport.hold.emptyTitle", "No one is on hold")}</b>
            {t(
              "challengeReport.hold.emptyBody",
              "If a number looks wrong, use Put on hold on that row. They come off the public board until you've checked it — nothing is deleted."
            )}
          </p>
        ) : (
          <ul>
            {held.map((member) => (
              <li key={member._id} className="challenge-report__held-row">
                <div>
                  <div>{member.name}</div>
                  <div className="challenge-report__held-sub">
                    {t(
                      "challengeReport.hold.heldOn",
                      "Held {{date}} · by {{name}}",
                      {
                        date: moment(member.heldAt).format("D MMM"),
                        name: member.heldByName,
                      }
                    )}
                  </div>
                </div>
                <div>
                  <div className="challenge-report__held-label">
                    {t("challengeReport.hold.why", "Why")}
                  </div>
                  {member.why ? (
                    <div>
                      <span className="challenge-report__unusual mr-1.5">
                        {t("challengeReport.hold.unusual", "Unusual")}
                      </span>
                      {member.why}
                    </div>
                  ) : (
                    <div className="challenge-report__held-sub">
                      {t("challengeReport.hold.manual", "Held manually")}
                    </div>
                  )}
                </div>
                <label className="mb-0!">
                  <span className="challenge-report__held-label">
                    {t("challengeReport.hold.note", "Note for your team")}
                  </span>
                  <input
                    type="text"
                    maxLength={500}
                    defaultValue={member.note}
                    placeholder={t(
                      "challengeReport.hold.notePlaceholder",
                      "e.g. Waiting on video"
                    )}
                    onBlur={(e) => {
                      const note = e.target.value.trim();
                      if (note !== member.note) {
                        hold({ userId: member._id, note });
                      }
                    }}
                  />
                </label>
                <div className="flex gap-2 justify-end">
                  {/* PUSH / gym coaches have no athlete portal for members. */}
                  {audience === "pull" && (
                    // biome-ignore lint/a11y/useSemanticElements: app.css styles link buttons by role
                    <a
                      role="button"
                      data-button-type="secondary"
                      href={`/athlete-portal/profile/?athlete=${member._id}&section=dashboard`}
                    >
                      {t("challengeReport.hold.reviewData", "Review data")}
                    </a>
                  )}
                  <button
                    type="button"
                    data-button-type="secondary"
                    onClick={() =>
                      hold({
                        userId: member._id,
                        name: member.name,
                        restore: "1",
                      })
                    }
                  >
                    {t("challengeReport.hold.return", "Return to board")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ))}

      {tab === "optedOut" &&
        (optedOut.length === 0 ? (
          <p className="challenge-report__held-empty">
            <b>
              {t(
                "challengeReport.hold.noOptedOutTitle",
                "No one has opted out"
              )}
            </b>
            {t(
              "challengeReport.hold.noOptedOutBody",
              "Athletes can hide themselves from the board in the app. They show up here, and only they can switch it back."
            )}
          </p>
        ) : (
          <>
            <p className="challenge-report__held-sub">
              {t(
                "challengeReport.hold.optedOutNote",
                "They hid themselves in the app. Only they can switch it back."
              )}
            </p>
            <ul>
              {optedOut.map((member) => (
                <li key={member._id} className="challenge-report__held-row">
                  <div>{member.name}</div>
                </li>
              ))}
            </ul>
          </>
        ))}
    </section>
  );
};

export default MemberPreviewTable;
